import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import { BiCheck, BiInfoCircle, BiSearch } from 'react-icons/bi';
import { FaExclamationTriangle } from 'react-icons/fa';

import { useAppSelector } from '@/app/hook';
import { useGetAllScenariosQuery } from '@/app/services/chatbot-rtk.services';
import ApiFailure from '@/components/ui/ApiFailure';
import { PSInput } from '@/components/ui/input';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { selectCurrentDataState } from '../../create.slice';
import DependencyVisualization from './DependencyVisualization';
import EnhancedBotDependency from './EnhancedBotDependency';
import SkeletonLoading from './SkeletonLoading';

const allowedChatbots = [BotTypeEnum.TEXT_SCENARIO, BotTypeEnum.VIDEO_SCENARIO, BotTypeEnum.AI_READY_TO_USE];

function Step2({ editData, disabled }: { editData?: any; disabled?: boolean }) {
  const { t } = useTranslation();
  const { isLoading, isError, isSuccess, refetch, data } = useGetAllScenariosQuery({ botType: 'widget' });

  const [selected, setSelected] = useState<
    {
      bot_id: string;
      name: string;
      type: string;
    }[]
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [validationError, setValidationError] = useState<string>('');
  const [showDependencyModal, setShowDependencyModal] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<{
    bot: { bot_id: string; name: string; type: string };
    dependencies: { bot_id: string; name: string; type: string }[];
  } | null>(null);
  const currentData = editData || useAppSelector(selectCurrentDataState);
  const { formState, setValue, trigger, clearErrors, setError } = useFormContext();

  useEffect(() => {
    setValue('configuration', {
      ...(currentData.configuration || {}),
      bots: selected || [],
    });

    // Check for missing dependencies
    const missingDeps = getMissingDependencies(selected);

    if (missingDeps.length > 0) {
      const message = t('marketplace/step2/validation/missingDependency', {
        deps: missingDeps.join(', '),
      });
      setValidationError(message);
      setError('configuration.bots', {
        type: 'manual',
        message: message,
      });
      return;
    }

    // Clear validation error when user selects chatbots
    if (selected.length > 0) {
      setValidationError('');
      clearErrors('configuration.bots');
    } else if (selected.length === 0) {
      setValidationError(t('marketplace/step2/validation/noBotsSelected'));
    }

    // Trigger validation for the configuration field
    trigger('configuration');
  }, [selected, setValue, trigger, clearErrors, t]);

  useEffect(() => {
    if (currentData?.configuration) {
      const newBots = currentData.configuration.bots || [];
      // Only update if bots are different
      const isDifferent = selected.length !== newBots.length || selected.some((bot, idx) => bot.bot_id !== newBots[idx]?.bot_id);
      if (isDifferent) {
        setValue('configuration', {
          ...(currentData.configuration || {}),
          bots: newBots,
        });
        setSelected(newBots);
      }
    }
  }, [currentData?.configuration?.bots, setValue]);

  const handleBotSelection = (chatbot: { bot_id: string; name: string; type: string }) => {
    const exists = selected.some(item => item.bot_id === chatbot.bot_id);

    if (exists) {
      // Remove from selection
      setSelected(prev => prev.filter(item => item.bot_id !== chatbot.bot_id));
      return;
    }

    // Find dependencies for the selected bot
    const botData = data?.find(b => b.id === chatbot.bot_id);
    const dependencyIds: string[] = botData?.dependencies?.dependsOn || [];

    // Find dependency bot objects (not already selected)
    const dependencyBots =
      data
        ?.filter(chatbot => allowedChatbots.includes(chatbot.type as BotTypeEnum))
        ?.filter(b => dependencyIds.includes(b.id) && !selected.some(item => item.bot_id === b.id))
        .map(b => ({
          bot_id: b.id,
          name: b.title as string,
          type: b.type,
        })) || [];

    // Calculate the new selection size
    const newSelectionCount = selected.length + 1 + dependencyBots.length;

    if (newSelectionCount > 10) {
      setValidationError(t('marketplace/step2/validation/maxBotsReached'));
      return;
    }

    // If there are dependencies, show preview modal
    if (dependencyBots.length > 0) {
      setPendingSelection({
        bot: chatbot,
        dependencies: dependencyBots,
      });
      setShowDependencyModal(true);
    } else {
      // No dependencies, add directly
      setSelected(prev => [...prev, chatbot]);
    }
  };

  const confirmDependencySelection = () => {
    if (pendingSelection) {
      setSelected(prev => [...prev, pendingSelection.bot, ...pendingSelection.dependencies]);
      setPendingSelection(null);
      setShowDependencyModal(false);
    }
  };

  const cancelDependencySelection = () => {
    setPendingSelection(null);
    setShowDependencyModal(false);
  };

  const getMissingDependencies = (selectedBots: { bot_id: string }[]) => {
    const missing: string[] = [];

    for (const bot of selectedBots) {
      const botData = data?.find(b => b.id === bot.bot_id);
      const dependencyIds: string[] = botData?.dependencies?.dependsOn || [];

      for (const depId of dependencyIds) {
        if (!selectedBots.some(sel => sel.bot_id === depId)) {
          // Find dependency name
          const depBot = data?.find(b => b.id === depId);
          if (depBot) {
            if (missing.find(x => x === depBot.title)) continue;
            missing.push(depBot.title);
          } else {
            missing.push(depId);
          }
        }
      }
    }

    return missing;
  };

  const filteredData =
    data?.filter(
      chatbot =>
        allowedChatbots.includes(chatbot.type as BotTypeEnum) &&
        (chatbot.displayedName?.toLowerCase().includes(searchTerm.toLowerCase()) || chatbot.title?.toLowerCase().includes(searchTerm.toLowerCase()))
    ) || [];

  let content;
  if (isLoading) {
    content = <SkeletonLoading length={6} />;
  } else if (isError) {
    content = (
      <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
        <ApiFailure refetch={refetch} />
      </div>
    );
  } else if (isSuccess) {
    content = (
      <div className="tw-space-y-4">
        {filteredData.length === 0 ? (
          <div className="tw-text-center tw-py-12">
            <div className="tw-text-gray-400 tw-text-lg tw-font-['Lexend']">{t('marketplace/step2/noChatbots/title')}</div>
            <p className="tw-text-gray-500 tw-text-sm tw-font-['Lexend'] tw-mt-1">{t('marketplace/step2/noChatbots/description')}</p>
          </div>
        ) : (
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
            {filteredData.map(chatbot => {
              const isSelected = selected.some(item => item.bot_id === chatbot.id);
              const isSelectionLimited = !isSelected && selected.length >= 10;

              return (
                <div
                  key={chatbot.id}
                  role="button"
                  tabIndex={isSelectionLimited || disabled ? -1 : 0}
                  onClick={() =>
                    !disabled && !isSelectionLimited && handleBotSelection({ bot_id: chatbot.id, name: chatbot.title as string, type: chatbot.type })
                  }
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      !disabled && !isSelectionLimited && handleBotSelection({ bot_id: chatbot.id, name: chatbot.title as string, type: chatbot.type });
                    }
                  }}
                  className={`tw-relative tw-flex tw-flex-col tw-p-6 tw-bg-white tw-rounded-xl tw-shadow-sm tw-border-2 tw-transition-all tw-duration-200 tw-min-h-[280px] ${
                    isSelectionLimited || disabled ? 'tw-cursor-not-allowed tw-opacity-50 tw-border-gray-200' : 'tw-cursor-pointer hover:tw-shadow-md'
                  } ${isSelected ? 'tw-border-blue-500 tw-ring-4 tw-ring-blue-500 tw-bg-blue-50' : 'tw-border-gray-200 hover:tw-border-blue-300'}`}
                >
                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="tw-absolute tw-top-4 tw-right-4 tw-w-7 tw-h-7 tw-bg-blue-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-z-10">
                      <BiCheck className="tw-w-4 tw-h-4 tw-text-white" />
                    </div>
                  )}

                  {/* Bot header section */}
                  <div className="tw-flex tw-items-start tw-gap-4 tw-my-4">
                    <img
                      src={chatbot.logo}
                      alt={chatbot.title}
                      className="tw-w-14 tw-h-14 tw-rounded-xl tw-object-cover tw-border tw-border-gray-200 tw-flex-shrink-0"
                    />
                    <div className="tw-flex-1 tw-min-w-0">
                      <h3 className="tw-font-semibold tw-text-gray-900 tw-font-['Lexend'] tw-text-lg tw-truncate tw-mb-1">{chatbot.title}</h3>
                      <p className="tw-text-sm tw-text-gray-600 tw-font-['Lexend'] tw-leading-relaxed">{Utilities.getBotNameByBotCode(chatbot.type, t)}</p>
                    </div>
                  </div>

                  {/* Dependencies section - separate area */}
                  <div className="tw-flex-1 tw-mb-4">
                    <EnhancedBotDependency item={chatbot} source={data} selected={selected} />
                  </div>

                  {/* Footer section - status and date */}
                  <div className="tw-flex tw-items-center tw-justify-between tw-pt-3 tw-border-t tw-border-gray-100 tw-mt-auto">
                    <span
                      className={`tw-text-xs tw-font-medium tw-px-3 tw-py-1.5 tw-rounded-full tw-font-['Lexend'] ${
                        chatbot.status === 'not_published' ? 'tw-bg-gray-100 tw-text-gray-700' : 'tw-bg-green-100 tw-text-green-700'
                      }`}
                    >
                      {chatbot.status === 'not_published' ? t('General/key15') : t('marketplace/step2/status/published')}
                    </span>
                    <span className="tw-text-xs tw-text-gray-500 tw-font-['Lexend']">{new Date(chatbot.creation_date_time).toLocaleDateString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="tw-max-w-4xl tw-mx-auto tw-p-6 tw-space-y-6">
      {/* Header */}
      {!editData && (
        <div className="tw-text-center tw-space-y-3">
          <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-font-['Lexend']">{t('marketplace/step2/header/title')}</h1>
          <p className="tw-text-lg tw-text-gray-600 tw-font-['Lexend']">{t('marketplace/step2/header/description')}</p>
        </div>
      )}

      {/* Selection info and search */}
      <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4 tw-p-4 tw-bg-blue-50 tw-rounded-xl">
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-2">
            <BiInfoCircle className="tw-w-5 tw-h-5 tw-text-blue-600" />
            <span className="tw-text-sm tw-font-medium tw-text-blue-900 tw-font-['Lexend']">
              {selected.length} {t('marketplace/step2/selection/of')} 10 {t('marketplace/step2/selection/selected')}
            </span>
          </div>
          {selected.length >= 10 && (
            <span className="tw-text-xs tw-bg-orange-100 tw-text-orange-700 tw-px-2 tw-py-1 tw-rounded-full tw-font-['Lexend']">
              {t('marketplace/step2/selection/maxReached')}
            </span>
          )}
        </div>

        <div className="tw-relative tw-w-full sm:tw-w-64">
          <BiSearch className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-4 tw-h-4 tw-text-gray-400" />
          <PSInput
            type="text"
            placeholder={t('marketplace/step2/search/placeholder')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="tw-border tw-border-solid tw-border-gray-300 tw-pl-10 tw-pr-4 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
          />
        </div>
      </div>
      {/* Requirements */}
      <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg">
        <p className="tw-text-sm tw-text-gray-700 tw-font-['Lexend']">
          <strong>{t('marketplace/step2/requirements/title')}</strong> {t('marketplace/step2/requirements/description')}
        </p>
      </div>
      {/* Content */}
      {content}
      {/* Validation Error Messages */}
      {(validationError || formState.errors.configuration?.bots) && (
        <div className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-flex tw-items-start tw-gap-3">
          <FaExclamationTriangle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
          <div className="tw-flex-1">
            <h4 className="tw-font-medium tw-text-red-800 tw-font-['Lexend'] tw-mb-1">{t('marketplace/step2/validation/title')}</h4>
            <p className="tw-text-sm tw-text-red-700 tw-font-['Lexend']">
              {ReactHtmlParser(validationError || (formState.errors.configuration?.bots?.message as string) || '')}
            </p>
          </div>
        </div>
      )}
      {/* Selection Summary */}
      {selected.length > 0 && !editData && (
        <div className="tw-p-4 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg">
          <div className="tw-flex tw-items-start tw-gap-3">
            <BiCheck className="tw-w-5 tw-h-5 tw-text-green-600 tw-flex-shrink-0 tw-mt-0.5" />
            <div className="tw-flex-1">
              <h4 className="tw-font-medium tw-text-green-800 tw-font-['Lexend'] tw-mb-2">{t('marketplace/step2/selectedBots/title')}</h4>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {selected.map((bot, index) => (
                  <span key={bot.bot_id} className="tw-text-xs tw-bg-green-100 tw-text-green-800 tw-px-2 tw-py-1 tw-rounded-full tw-font-['Lexend']">
                    {bot.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dependency Visualization Modal */}
      {showDependencyModal && pendingSelection && (
        <DependencyVisualization
          selectedBot={pendingSelection.bot}
          dependencies={pendingSelection.dependencies}
          allBots={data || []}
          onConfirm={confirmDependencySelection}
          onCancel={cancelDependencySelection}
        />
      )}
    </div>
  );
}
export default WrappedErrorBoundary(Step2);
